/**
 * OperatorDetailPage — Task 25.3 (DEC-114). PLATFORM_ADMIN operator detail.
 *
 * Header (official name read-only + state/type/status + Deactivate/Reactivate), then the
 * Operator portal (plan, users, invite) and which haulers it sees — first, because it is
 * what Paelode comes here to set up and it used to sit below thousands of wells — then the
 * Display name & abbreviation editor (with a live HaulId preview), Possible Duplicates
 * (each with its verdict; Merge… opens the shared MergeDialog), the Wells section (collapsed until opened; server-side
 * search by well name or API number; sortable headers, OPEN-204; 50/page), and a read-only Haulers list.
 */
import * as React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '@lodex-pro/web-shared';
import { useBackTo, useUrlSort, type SortState } from '../../lib/url-state';
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Table, TableHeader, TableBody, TableRow, TableCell } from 'src/components/ui/table';
import { SortableHead } from 'src/components/ui/sortable-head';
import { Button } from 'src/components/ui/button';
import { FormRow, fieldStyle, labelStyle, subText, Pill, haulIdPreview } from '../../ui/bits';
import { OperatorPortalCard } from './OperatorPortalCard';
import { MergeDialog } from './MergeDialog';
import {
  useOperator,
  useUpdateOperator,
  useDeactivateOperator,
  useReactivateOperator,
  useOperatorWells,
  OPERATOR_WELL_SORTS,
  type OperatorSummary,
  type OperatorWellSort,
  type DuplicateOperator,
  type WellsPage,
} from './hooks';
import { OperatorHaulersCard } from './OperatorHaulersCard';
import { ABBREVIATION_RE, ABBREVIATION_RULE_TEXT } from '@lodex-pro/shared/abbreviation';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** The wells grid's default sort: the API's order (and the detail payload's first page). */
const WELLS_DEFAULT_SORT: SortState<OperatorWellSort> = { key: 'wellName', dir: 'asc' };

function Card({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
        marginBottom: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '14px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function EditSection({ operator }: { operator: OperatorSummary }): React.JSX.Element {
  const [displayName, setDisplayName] = React.useState(operator.displayName ?? '');
  const [abbrev, setAbbrev] = React.useState(operator.abbreviation ?? '');
  const [error, setError] = React.useState<string | null>(null);
  const update = useUpdateOperator(operator.id);

  const save = (): void => {
    if (abbrev !== '' && !ABBREVIATION_RE.test(abbrev.trim())) {
      setError(ABBREVIATION_RULE_TEXT);
      return;
    }
    setError(null);
    update.mutate(
      {
        displayName: displayName.trim() === '' ? null : displayName.trim(),
        abbreviation: abbrev.trim() === '' ? null : abbrev.trim().toUpperCase(),
      },
      {
        onSuccess: () => toast.success('Operator updated'),
        onError: () =>
          setError('Could not save — the abbreviation may already be taken by another operator.'),
      },
    );
  };

  return (
    <Card title="Display Name & Abbreviation">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <FormRow
          label="Display name"
          htmlFor="op-display"
          hint="Haulers will see this name instead of the official OCC name."
        >
          <input
            id="op-display"
            value={displayName}
            onChange={(e): void => setDisplayName(e.target.value)}
            placeholder="e.g. Devon Energy"
            style={fieldStyle}
          />
        </FormRow>
        <FormRow
          label="Abbreviation (letters only, 2–4)"
          htmlFor="op-abbrev"
          hint={`Tickets will look like: ${haulIdPreview(abbrev)}`}
        >
          <input
            id="op-abbrev"
            value={abbrev}
            maxLength={4}
            onChange={(e): void => setAbbrev(e.target.value.toUpperCase())}
            placeholder="DVN"
            style={{ ...fieldStyle, textTransform: 'uppercase', maxWidth: '140px' }}
          />
        </FormRow>
      </div>
      {error !== null && (
        <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)', marginTop: '12px' }}>
          {error}
        </p>
      )}
      <div style={{ marginTop: '16px' }}>
        <Button
          type="button"
          disabled={update.isPending}
          onClick={save}
          style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </Card>
  );
}

/**
 * Wells: COLLAPSED by default (a big operator has thousands, and Corbin missed the portal
 * section below them). The header carries the count and opens it. Search is server-side
 * (GET /:id/wells?q=, well name or API number) because the list is paged; page 1 with no
 * search comes from the detail payload.
 */
function WellsSection({
  operatorId,
  firstPage,
}: {
  operatorId: string;
  firstPage: WellsPage;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  // OPEN-204 — the API sorts and pages. The sort has its own URL names (?wells=&wellsDir=), and
  // the page is local state, so a header click resets it in the same update (like a new search,
  // no request goes out for the old page under the new sort).
  const { sort, toggle } = useUrlSort(OPERATOR_WELL_SORTS, WELLS_DEFAULT_SORT, [], 'wells');
  const onSort = (column: OperatorWellSort): void => {
    setPage(1);
    toggle(column);
  };
  const sortKey = sort?.key ?? WELLS_DEFAULT_SORT.key;
  const sortDir = sort?.dir ?? WELLS_DEFAULT_SORT.dir;
  const isDefaultSort = sortKey === WELLS_DEFAULT_SORT.key && sortDir === WELLS_DEFAULT_SORT.dir;
  const q = useDebouncedValue(search.trim());
  React.useEffect(() => setPage(1), [q]);
  // Page 1 by well name, unsearched, is in the detail payload.
  const fromPayload = q === '' && page === 1 && isDefaultSort;
  const fetched = useOperatorWells(
    operatorId,
    page,
    open && !fromPayload,
    q,
    isDefaultSort ? null : { key: sortKey, dir: sortDir },
  );
  const data = fromPayload ? firstPage : fetched.data;
  const pageCount = data === undefined ? 1 : Math.max(1, Math.ceil(data.total / data.pageSize));
  const bodyId = `wells-${operatorId}`;

  return (
    <section
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
        marginBottom: '16px',
      }}
    >
      <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={(): void => setOpen((o) => !o)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'inherit',
            font: 'inherit',
            cursor: 'pointer',
          }}
        >
          {open ? (
            <ChevronDown size={16} aria-hidden="true" />
          ) : (
            <ChevronRight size={16} aria-hidden="true" />
          )}
          Wells ({firstPage.total.toLocaleString('en-US')})
        </button>
      </h3>
      {open && (
        <div id={bodyId} style={{ marginTop: '14px' }}>
          {firstPage.total === 0 ? (
            <p style={{ ...subText, textTransform: 'none' }}>No active wells for this operator.</p>
          ) : (
            <>
              <input
                type="search"
                aria-label="Search wells by name or API number"
                placeholder="Search by well name or API number"
                value={search}
                onChange={(e): void => setSearch(e.target.value)}
                style={{ ...fieldStyle, maxWidth: '360px', marginBottom: '12px' }}
              />
              {data === undefined ? (
                <p style={subText}>{fetched.isError ? 'Could not load wells.' : 'Loading…'}</p>
              ) : data.total === 0 ? (
                <p role="status" style={{ ...subText, textTransform: 'none' }}>
                  No wells match “{q}”.
                </p>
              ) : (
                <>
                  {q !== '' && (
                    <p role="status" style={{ ...subText, textTransform: 'none' }}>
                      {data.total.toLocaleString('en-US')} {data.total === 1 ? 'match' : 'matches'}
                    </p>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableHead column="wellName" sort={sort} onSort={onSort}>
                          Well name
                        </SortableHead>
                        <SortableHead column="apiNumber" sort={sort} onSort={onSort}>
                          API number
                        </SortableHead>
                        <SortableHead column="state" sort={sort} onSort={onSort}>
                          State
                        </SortableHead>
                        <SortableHead column="county" sort={sort} onSort={onSort}>
                          County
                        </SortableHead>
                        <SortableHead column="wellType" sort={sort} onSort={onSort}>
                          Type
                        </SortableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.map((w) => (
                        <TableRow key={w.id}>
                          <TableCell style={{ fontWeight: 500 }}>{w.wellName}</TableCell>
                          <TableCell style={subText}>{w.apiNumber}</TableCell>
                          <TableCell>{w.state}</TableCell>
                          <TableCell>{w.county ?? '—'}</TableCell>
                          <TableCell>{w.wellType ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {pageCount > 1 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginTop: '12px',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        type="button"
                        variant="outline"
                        disabled={page <= 1}
                        onClick={(): void => setPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <span style={subText}>
                        Page {formatNumber(page)} of {formatNumber(pageCount)}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={page >= pageCount}
                        onClick={(): void => setPage((p) => Math.min(pageCount, p + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

export function OperatorDetailPage(): React.JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  // Back to the list as it was left: its search and filters (lib/url-state.ts).
  const backTo = useBackTo('/operators');
  const { data, isLoading, isError } = useOperator(id);
  const deactivate = useDeactivateOperator(id);
  const reactivate = useReactivateOperator(id);
  const [mergeSource, setMergeSource] = React.useState<DuplicateOperator | null>(null);

  if (isLoading) return <p style={{ ...subText, padding: '24px' }}>Loading operator…</p>;
  if (isError || data === undefined) {
    return (
      <div style={{ padding: '24px' }}>
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load this operator.
        </p>
        <Button type="button" variant="outline" onClick={(): void => void navigate(backTo)}>
          Back to operators
        </Button>
      </div>
    );
  }

  const { operator, haulers, possibleDuplicates, duplicateVerdict, wells, mergedFrom } = data;
  // Pre-select the group's suggested survivor as KEEP: the duplicate when it is the survivor,
  // else this row; none when both rows are in use (the dialog makes him choose).
  const keepFor = (d: DuplicateOperator): string | null =>
    d.isSuggestedSurvivor
      ? d.id
      : duplicateVerdict?.kind === 'BOTH_IN_USE' || d.verdict?.kind === 'BOTH_IN_USE'
        ? null
        : operator.id;

  const onDeactivate = (): void => {
    const msg =
      operator.haulerCount > 0
        ? `Deactivate ${operator.name}? ${formatNumber(operator.haulerCount)} hauler(s) still reference it — their links are not changed.`
        : `Deactivate ${operator.name}?`;
    if (!window.confirm(msg)) return;
    deactivate.mutate(
      {},
      {
        onSuccess: () => toast.success('Operator deactivated'),
        onError: () => toast.error('Could not deactivate'),
      },
    );
  };
  const onReactivate = (): void => {
    reactivate.mutate(
      {},
      {
        onSuccess: () => toast.success('Operator reactivated'),
        onError: () => toast.error('Could not reactivate'),
      },
    );
  };

  return (
    <div style={{ padding: '24px', maxWidth: '960px', margin: '0 auto' }}>
      <button
        type="button"
        onClick={(): void => void navigate(backTo)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: 'none',
          color: 'var(--color-text-secondary)',
          cursor: 'pointer',
          fontSize: '13px',
          marginBottom: '12px',
          padding: 0,
        }}
      >
        <ArrowLeft size={16} aria-hidden="true" /> Operators
      </button>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
          gap: '16px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>{operator.name}</h2>
            {operator.state !== null && <Pill label={operator.state} tone="neutral" />}
            {operator.isProducer && <Pill label="Producer" tone="accent" />}
            {operator.isDisposal && <Pill label="Disposal" tone="accent" />}
            {operator.mergedInto !== null ? (
              <Link
                to={`/operators/${operator.mergedInto.id}`}
                style={{ fontSize: '13px', color: 'var(--color-link)' }}
              >
                Merged into {operator.mergedInto.name}
              </Link>
            ) : (
              <Pill
                label={operator.isActive ? 'Active' : 'Inactive'}
                tone={operator.isActive ? 'positive' : 'muted'}
              />
            )}
          </div>
          {/* Corbin, 2026-10-30: which duplicates were merged into this row (kept as aliases). */}
          {mergedFrom.length > 0 && (
            <p style={{ ...subText, marginTop: '4px', textTransform: 'none' }}>
              Merged from:{' '}
              {mergedFrom.map((m, i) => (
                <React.Fragment key={m.id}>
                  {i > 0 && ', '}
                  <Link to={`/operators/${m.id}`} style={{ color: 'var(--color-link)' }}>
                    {m.name}
                  </Link>
                </React.Fragment>
              ))}
            </p>
          )}
          {operator.regulatorOperatorId !== null && (
            <p style={{ ...subText, marginTop: '4px' }}>
              Official OCC/RRC record · operator id {operator.regulatorOperatorId}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {operator.isActive ? (
            <Button
              type="button"
              variant="outline"
              disabled={deactivate.isPending}
              onClick={onDeactivate}
              style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              type="button"
              disabled={reactivate.isPending}
              onClick={onReactivate}
              style={{
                backgroundColor: 'var(--color-accent-blue)',
                color: 'var(--color-on-accent)',
              }}
            >
              Reactivate
            </Button>
          )}
        </div>
      </div>

      {/* Operator portal rework PR 1 — Paelode sets up the portal and its users. */}
      {operator.isActive && operator.mergedIntoId === null && (
        <OperatorPortalCard
          platformOperatorId={operator.id}
          operatorName={operator.displayName ?? operator.name}
        />
      )}

      {/* Operator portal PR 3 (0206): which haulers' data this operator sees. */}
      <OperatorHaulersCard platformOperatorId={operator.id} />

      <EditSection operator={operator} />

      {/* Possible duplicates */}
      {possibleDuplicates.length > 0 && (
        <Card title={`Possible Duplicates (${possibleDuplicates.length})`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {possibleDuplicates.map((d) => (
              <div
                key={d.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderRadius: '12px',
                  backgroundColor: 'var(--color-bg-tertiary)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontWeight: 500 }}>{d.name}</span>
                  {d.abbreviation !== null && <Pill label={d.abbreviation} tone="neutral" />}
                  <span style={subText}>
                    {d.state ?? '—'} · {formatNumber(d.wellCount)} wells ·{' '}
                    {formatNumber(d.haulerCount)} haulers
                  </span>
                  {d.isSuggestedSurvivor && <Pill label="Suggested keep" tone="positive" />}
                  {d.verdict !== null && <span style={{ fontSize: '13px' }}>{d.verdict.text}</span>}
                </div>
                <Button type="button" variant="outline" onClick={(): void => setMergeSource(d)}>
                  Merge…
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <WellsSection operatorId={operator.id} firstPage={wells} />

      {/* Haulers (read-only) */}
      <Card title={`Haulers (${haulers.length})`}>
        {haulers.length === 0 ? (
          <p style={{ ...subText, textTransform: 'none' }}>No haulers use this operator yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {haulers.map((h) => (
              <div
                key={h.tenantId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--color-border-secondary)',
                  fontSize: '14px',
                }}
              >
                <span style={{ fontWeight: 500 }}>{h.tenantName}</span>
                <Pill label={h.abbreviation} tone="neutral" />
                {!h.isActive && <Pill label="Inactive" tone="muted" />}
              </div>
            ))}
          </div>
        )}
        <p style={{ ...subText, marginTop: '10px', textTransform: 'none' }}>
          Read-only. Each hauler manages its own operator relationship.
        </p>
      </Card>

      <MergeDialog
        pair={
          mergeSource === null
            ? null
            : [
                { id: operator.id, name: operator.name, state: operator.state },
                { id: mergeSource.id, name: mergeSource.name, state: mergeSource.state },
              ]
        }
        suggestedKeepId={mergeSource === null ? null : keepFor(mergeSource)}
        onOpenChange={(): void => setMergeSource(null)}
        onMerged={(keepId): void => {
          // This row became an alias: go to the row it was merged into.
          if (keepId !== operator.id) void navigate(`/operators/${keepId}`, { replace: true });
        }}
      />
    </div>
  );
}
